import React, { useEffect, useState, useMemo } from "react";
import { Modal } from "@material-ui/core";
import { CircularProgress } from "@material-ui/core";
import { apiReports } from "../api/reports";
import CountyChoroplethMap from "./CountyChoroplethMap";

const pct = (v) => `${Math.round((v || 0) * 100)}%`;

const METRICS = [
  { key: "complete", label: "Scheduled" },
];

const currentUtcMonth = () => {
  const n = new Date();
  return `${n.getUTCFullYear()}-${String(n.getUTCMonth() + 1).padStart(2, "0")}`;
};

// SchedulerAreaUtilisationDetailModal — drill-down for a scheduler-area row.
// County choropleth (scheduled) + per-county table + per-scheduler
// split. workAreaId 0 is the Unassigned (out-of-area) sentinel.
const SchedulerAreaUtilisationDetailModal = ({ open, onClose, workAreaId, days, month }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [metric, setMetric] = useState("complete");

  useEffect(() => {
    if (!open || workAreaId == null) {return;} // 0 is the Unassigned sentinel
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);
    apiReports.getSchedulerAreaUtilisationDetail(workAreaId, days, month)
      .then((res) => {
        if (cancelled) {return;}
        const r = res?.data?.result;
        if (res?.data?.status === 200 && r) {
          setDetail(r);
        } else {
          setError(res?.data?.message || "Failed to load detail.");
        }
        setLoading(false);
      })
      .catch(() => { if (!cancelled) { setError("Failed to load detail."); setLoading(false); } });
    return () => { cancelled = true; };
  }, [open, workAreaId, days, month]);

  const counties = detail?.counties || [];
  const sortedCounties = useMemo(
    () => [...counties].sort((a, b) => (b[metric] || 0) - (a[metric] || 0)),
    [counties, metric]
  );

  return (
    <Modal open={open} onClose={onClose}>
      <div style={{
        top: "50%", left: "50%", transform: "translate(-50%, -50%)",
        position: "absolute", maxWidth: 1040, width: "94%",
        maxHeight: "90vh", overflowY: "auto",
      }} className="tw-bg-white tw-rounded-xl tw-shadow-lg tw-p-6 tw-outline-none">
        <div className="tw-flex tw-justify-between tw-items-start tw-mb-4">
          <div>
            <h2 className="tw-text-2xl tw-font-semibold">{detail?.name || "Area"}</h2>
            {detail && <div className="tw-text-sm tw-text-gray-600 tw-mt-1">
              {month
                ? `Policies scheduled · Calendar month ${month}${month === currentUtcMonth() ? " (to date)" : ""}`
                : `Policies scheduled · Last ${detail.windowDays} days`}
            </div>}
          </div>
          <button onClick={onClose} className="tw-text-xl tw-px-2">✕</button>
        </div>

        {loading && <div className="tw-flex tw-justify-center tw-py-10"><CircularProgress /></div>}
        {error && <div className="tw-text-fs-red tw-py-6 tw-text-center">{error}</div>}

        {detail &&
          <>
            <div className="tw-grid tw-grid-cols-2 tw-gap-4 tw-mb-6">
              <Stat label="Scheduled" value={detail.scheduled} color="tw-bg-fs-green" />
              <Stat label="Counties" value={counties.length} color="tw-bg-primary" />
            </div>

            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
              <span className="tw-text-sm tw-text-gray-600">Shade counties by:</span>
              {METRICS.map((m) =>
                <button key={m.key} onClick={() => setMetric(m.key)}
                  className={`tw-px-3 tw-py-1 tw-rounded-lg tw-text-sm ${metric === m.key ? "tw-bg-primary tw-text-white" : "tw-bg-gray-200 tw-text-gray-700"}`}>
                  {m.label}
                </button>
              )}
            </div>

            <div className="tw-bg-gray-50 tw-p-3 tw-rounded-lg tw-mb-6">
              {counties.length > 0
                ? <CountyChoroplethMap counties={counties} metric={metric} />
                : <div className="tw-text-gray-500 tw-text-sm tw-py-10 tw-text-center">No counties in this area.</div>}
            </div>

            <div className="tw-grid md:tw-grid-cols-2 tw-gap-6">
              <div>
                <h3 className="tw-font-semibold tw-mb-2">Counties</h3>
                <div className="tw-overflow-y-auto" style={{ maxHeight: 320 }}>
                  <table className="tw-min-w-full tw-text-sm">
                    <thead className="tw-sticky tw-top-0 tw-bg-white">
                      <tr className="tw-border-b tw-border-gray-200">
                        <th className="tw-px-2 tw-py-1 tw-text-left">County</th>
                        <th className="tw-px-2 tw-py-1 tw-text-right">Scheduled</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedCounties.map((c) =>
                        <tr key={`${c.state}-${c.county}`} className="tw-border-b tw-border-gray-100">
                          <td className="tw-px-2 tw-py-1">
                            {c.county || "(blank)"}<span className="tw-text-gray-400 tw-text-xs">, {c.state}</span>
                          </td>
                          <td className="tw-px-2 tw-py-1 tw-text-right">{c.complete}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3 className="tw-font-semibold tw-mb-2">Schedulers</h3>
                {(detail.schedulers || []).length === 0
                  ? <p className="tw-text-gray-500 tw-text-sm">No schedulers in this area.</p>
                  : <div className="tw-overflow-y-auto" style={{ maxHeight: 320 }}>
                    <table className="tw-min-w-full tw-text-sm">
                      <thead className="tw-sticky tw-top-0 tw-bg-white">
                        <tr className="tw-border-b tw-border-gray-200">
                          <th className="tw-px-2 tw-py-1 tw-text-left">Scheduler</th>
                          <th className="tw-px-2 tw-py-1 tw-text-right">Scheduled</th>
                          <th className="tw-px-2 tw-py-1 tw-text-right">%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.schedulers.map((s) =>
                          <tr key={s.userId} className="tw-border-b tw-border-gray-100">
                            <td className="tw-px-2 tw-py-1">{s.name}</td>
                            <td className="tw-px-2 tw-py-1 tw-text-right">{s.scheduled}</td>
                            <td className="tw-px-2 tw-py-1 tw-text-right">{pct(s.pct)}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>}
              </div>
            </div>
          </>
        }
      </div>
    </Modal>
  );
};

const Stat = ({ label, value, color }) =>
  <div className={`tw-rounded-lg tw-p-3 tw-text-white ${color}`}>
    <div className="tw-text-2xl tw-font-bold">{value}</div>
    <div className="tw-text-xs tw-uppercase tw-tracking-wider tw-opacity-90">{label}</div>
  </div>
;

export default SchedulerAreaUtilisationDetailModal;